import {
	Alert,
	Button,
	Group,
	Loader,
	Paper,
	Stack,
	Text,
	Title,
} from "@mantine/core";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { describeApiErrorSync } from "@/shared/api/error-message";

export function JournalShell({
	title,
	subtitle,
	filters,
	isLoading,
	error,
	errorTitle,
	onRetry,
	empty,
	hasRows,
	page,
	total,
	hasNextPage,
	isFetching,
	onPageChange,
	children,
}: {
	title: string;
	subtitle: string;
	filters: ReactNode;
	isLoading: boolean;
	error: unknown;
	errorTitle: string;
	onRetry?: () => void;
	empty: string;
	hasRows: boolean;
	page: number;
	total: number;
	hasNextPage: boolean;
	isFetching?: boolean;
	onPageChange: (page: number | ((value: number) => number)) => void;
	children: ReactNode;
}) {
	const { t } = useTranslation();
	return (
		<Stack gap="md">
			<div>
				<Title order={2}>{title}</Title>
				<Text c="dimmed" size="sm">
					{subtitle}
				</Text>
			</div>
			<Paper p="md" radius="lg" withBorder>
				<Stack gap="md">
					{filters}
					{isLoading ? (
						<Group justify="center" py="xl">
							<Loader />
						</Group>
					) : null}
					{error ? (
						<Alert color="red" title={errorTitle}>
							<Text size="sm">{describeApiErrorSync(error)}</Text>
							{onRetry ? (
								<Button variant="light" mt="sm" onClick={onRetry}>
									{t("common.retry")}
								</Button>
							) : null}
						</Alert>
					) : null}
					{!isLoading && !error && !hasRows ? (
						<Text c="dimmed" ta="center" py="xl">
							{empty}
						</Text>
					) : null}
					{hasRows ? children : null}
					<Group justify="space-between">
						<Text size="sm" c="dimmed">
							{t("common.total", { count: total })} · {t("common.page")} {page}
						</Text>
						<Group>
							<Button
								variant="default"
								disabled={page <= 1 || Boolean(isFetching)}
								onClick={() => onPageChange((value) => Math.max(1, value - 1))}
							>
								{t("common.previous")}
							</Button>
							<Button
								variant="default"
								disabled={!hasNextPage || Boolean(isFetching)}
								onClick={() => onPageChange((value) => value + 1)}
							>
								{t("common.next")}
							</Button>
						</Group>
					</Group>
				</Stack>
			</Paper>
		</Stack>
	);
}
